import React from "react";

import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Link,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import {
  DashboardIcon,
  DatabaseIcon,
  CacheIcon,
  QueueIcon,
  CloudIcon,
} from "@shell/components/icons";
import { FOCUS_RING } from "@shell/styles/designTokens";

import type { InfraConfigUI, ResourceCreateType } from "../../types";

export interface EmptyProjectHubProps {
  infraConfig: InfraConfigUI | null;
  infraLoading: boolean;
  onConnectCloud: () => void;
  onSetUp: () => void;
  onDeployApp: () => void;
  onAdoptStack: () => void;
  onCreateResource: (type: ResourceCreateType) => void;
  onManage: () => void;
}

interface HubCard {
  key: string;
  label: string;
  description: string;
  icon: React.ReactElement;
  recommended?: boolean;
  onClick: () => void;
}

export function EmptyProjectHub({
  infraConfig,
  infraLoading,
  onConnectCloud,
  onDeployApp,
  onAdoptStack,
  onCreateResource,
}: EmptyProjectHubProps): React.ReactElement {
  const theme = useTheme();
  const isConnected = !!infraConfig && !!infraConfig.cloudProvider;

  const cards: HubCard[] = [
    {
      key: "app",
      label: "Deploy Application",
      description: "Full-stack apps with UI, API, and services",
      icon: <DashboardIcon size={24} />,
      recommended: true,
      onClick: onDeployApp,
    },
    {
      key: "db",
      label: "Add Database",
      description: "PostgreSQL on shared or dedicated cluster",
      icon: <DatabaseIcon size={24} />,
      onClick: () => onCreateResource("db"),
    },
    {
      key: "cache",
      label: "Add Cache",
      description: "Redis in-memory with persistence options",
      icon: <CacheIcon size={24} />,
      onClick: () => onCreateResource("cache"),
    },
    {
      key: "queue",
      label: "Add Queue",
      description: "RabbitMQ, NATS, Kafka, or Pulsar messaging",
      icon: <QueueIcon size={24} />,
      onClick: () => onCreateResource("queue"),
    },
  ];

  if (infraLoading) {
    return (
      <Stack spacing={2} sx={{ py: 4 }}>
        <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack spacing={3} sx={{ py: 2 }}>
      {/* Cloud connection banner — only when not connected */}
      {!isConnected && (
        <Card
          sx={{
            p: 3,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: "primary.main",
                  flexShrink: 0,
                }}
              >
                <CloudIcon size={24} />
              </Box>
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  Set up project infrastructure
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Infrastructure is the execution target where Rusha runs your
                  databases, caches, queues, and applications. Choose Rusha
                  Hosted or connect your own cloud provider.
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="contained"
                size="small"
                onClick={onConnectCloud}
                sx={{ textTransform: "none" }}
              >
                Set up infrastructure
              </Button>
              <Button
                size="small"
                onClick={onConnectCloud}
                sx={{ textTransform: "none" }}
              >
                Use Rusha Hosted
              </Button>
            </Stack>
          </Stack>
        </Card>
      )}

      {/* Section heading */}
      <Box sx={{ textAlign: "center", py: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          Start building your project
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Choose a starting point below. You can always add more later.
        </Typography>
      </Box>

      {/* Card grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        {cards.map((card) => (
          <Card
            key={card.key}
            onClick={card.onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                card.onClick();
              }
            }}
            sx={{
              cursor: "pointer",
              borderRadius: 2,
              transition: "all 150ms ease",
              border: card.recommended
                ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                : `1px solid ${theme.palette.divider}`,
              "&:hover": {
                borderColor: alpha(theme.palette.primary.main, 0.3),
                transform: "translateY(-1px)",
                boxShadow: theme.shadows[4],
              },
              "&:focus-visible": {
                outline: "none",
                boxShadow: `0 0 0 ${FOCUS_RING.OFFSET}px ${theme.palette.background.paper}, 0 0 0 ${FOCUS_RING.OFFSET + FOCUS_RING.WIDTH}px ${theme.palette.primary.main}`,
              },
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: "primary.main",
                    flexShrink: 0,
                  }}
                >
                  {card.icon}
                </Box>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1" fontWeight={600}>
                      {card.label}
                    </Typography>
                    {card.recommended && (
                      <Chip
                        label="Recommended"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{
                          fontSize: "0.7rem",
                          height: 20,
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    )}
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {card.description}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Button
        variant="outlined"
        onClick={onAdoptStack}
        sx={{ alignSelf: "center", textTransform: "none" }}
      >
        Adopt stack
      </Button>

      {/* Info alert with learn-more link */}
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        <Typography variant="body2">
          Applications auto-provision and connect all necessary resources.
          Standalone resources are best when you need custom architecture.{" "}
          <Link
            href="#"
            onClick={(e) => e.preventDefault()}
            sx={{ fontWeight: 500 }}
          >
            Learn more →
          </Link>
        </Typography>
      </Alert>
    </Stack>
  );
}
